import { createAsyncThunk } from '@reduxjs/toolkit'

import { getFromSettingsServer, putDataToSettingsServer } from 'src/apiHelper/settingsApi'
import { logger } from 'src/utils/logger'

import {
  currentFeatures,
  currentPermissions,
  currentPractice,
  currentProvider,
  currentRoles,
  currentSettings,
  getPractice,
  getProviders,
} from 'src/helper/backendHelper'

import { SETTINGS_URL } from '../../apiHelper/apiRoute/settings'
import {
  loginOmniauth,
  setPracticeProvider,
  validateToken,
  verifyMfaPasscode,
} from '../../helper/omniauthHelper'

async function processLogin(loginResponse) {
  const user = await validateToken(loginResponse?.access_token)

  if (user?.status === 404) {
    return {}
  }

  let authUser = {
    authRefreshToken: loginResponse?.refresh_token,
    authToken: loginResponse?.access_token,
    user: {
      _id: user?.user_id,
      username: user?.username,
      displayname: user?.display_name,
      isInternal: user?.is_internal,
      tosAccepted: user?.tos_accepted,
    },
  }
  localStorage.setItem('authUser', JSON.stringify(authUser))

  const results = await Promise.allSettled([
    getFromSettingsServer(SETTINGS_URL.GET_PRACTICE).then((res) => res?.results),
    getFromSettingsServer(SETTINGS_URL.GET_PROVIDER).then((res) => res?.results),
  ])
  const [practiceList, providerList] = results.map((result) => {
    if (result.status === 'fulfilled') {
      return result.value
    }
    console.error(result.reason)
    return null
  })
  return {
    ...loginResponse,
    practiceList,
    providerList,
    user: authUser.user,
  }
}

export const login = createAsyncThunk('user/login', async (credential, thunkAPI) => {
  try {
    const data = {
      username: btoa(credential.username),
      password: btoa(credential.password),
    }
    const response = await loginOmniauth(data)

    if (response?.access_token) {
      return await processLogin(response)
    } else if (response?.mfa_token) {
      return {
        ...response,
        username: credential?.username,
      }
    } else {
      return thunkAPI.rejectWithValue(response || {})
    }
  } catch (err) {
    console.log('login failed::', err.message)
    return err.message
  }
})

/**
 * Redux thunk for handling SSO (Single Sign-On) login after successful OIDC callback
 *
 * This thunk processes the SSO response from the omniauth-api after the user has been
 * authenticated through the identity provider. It validates the response, stores authentication
 * tokens, fetches user's practice and provider lists, and prepares the user session.
 *
 * @async
 * @function ssoLogin
 * @param {Object} ssoResponse - The SSO response object from the OIDC callback endpoint
 * @param {Object} ssoResponse.token_holder - Container for authentication tokens
 * @param {string} ssoResponse.token_holder.access_token - JWT access token for API authentication
 * @param {string} ssoResponse.token_holder.refresh_token - JWT refresh token for token renewal
 * @param {Object} ssoResponse.user_details - User information from the identity provider
 * @param {string} ssoResponse.user_details.user_id - Unique user identifier
 * @param {string} ssoResponse.user_details.username - User's username/email
 * @param {string} [ssoResponse.user_details.display_name] - User's display name
 * @param {boolean} [ssoResponse.user_details.is_internal] - Whether user is internal staff
 * @param {boolean} [ssoResponse.user_details.tos_accepted] - Whether user accepted terms of service
 * @param {string} ssoResponse.result_code - Result code from SSO operation (LOGIN_SUCCESS, ACCOUNT_LINKING, ERROR)
 * @param {Object} thunkAPI - Redux Toolkit thunk API object
 *
 * @returns {Promise<Object>} Resolved login data containing tokens, user info, and lists
 * @returns {string} return.access_token - JWT access token
 * @returns {string} return.refresh_token - JWT refresh token
 * @returns {string} return.result_code - SSO operation result code
 * @returns {Array} return.practiceList - List of practices user has access to
 * @returns {Array} return.providerList - List of providers user has access to
 * @returns {Object} return.user - User information object
 * @returns {string} return.user._id - User ID
 * @returns {string} return.user.username - Username
 * @returns {string} return.user.displayname - Display name
 * @returns {boolean} return.user.isInternal - Internal staff flag
 * @returns {boolean} return.user.tosAccepted - Terms of service acceptance flag
 *
 * @throws {Object} Rejected with error message if validation fails or operation errors
 *
 * @example
 * // Dispatch from a component after SSO callback
 * const result = await dispatch(ssoLogin({
 *   token_holder: {
 *     access_token: 'eyJhbGc...',
 *     refresh_token: 'eyJhbGc...'
 *   },
 *   user_details: {
 *     user_id: '507f1f77bcf86cd799439011',
 *     username: 'doctor@example.com',
 *     display_name: 'Dr. Smith',
 *     is_internal: false,
 *     tos_accepted: true
 *   },
 *   result_code: 'LOGIN_SUCCESS'
 * }))
 */
export const ssoLogin = createAsyncThunk('user/ssoLogin', async (ssoResponse, thunkAPI) => {
  try {
    const { token_holder, user_details, result_code } = ssoResponse

    // Validate required fields
    if (!token_holder?.access_token || !token_holder?.refresh_token) {
      logger.error('[SSO Login] Response missing required tokens')
      return thunkAPI.rejectWithValue({
        message: 'Invalid SSO response - missing authentication tokens',
      })
    }

    if (!user_details?.user_id || !user_details?.username) {
      logger.error('[SSO Login] Response missing required user details')
      return thunkAPI.rejectWithValue({
        message: 'Invalid SSO response - missing user information',
      })
    }

    // Store auth user in localStorage
    const authUser = {
      authRefreshToken: token_holder.refresh_token,
      authToken: token_holder.access_token,
      user: {
        _id: user_details.user_id,
        username: user_details.username,
        displayname: user_details.display_name || '',
        isInternal: user_details.is_internal || false,
        tosAccepted: user_details.tos_accepted || false,
      },
    }
    localStorage.setItem('authUser', JSON.stringify(authUser))

    // Fetch practice and provider lists
    const results = await Promise.allSettled([
      getFromSettingsServer(SETTINGS_URL.GET_PRACTICE).then((res) => res?.results),
      getFromSettingsServer(SETTINGS_URL.GET_PROVIDER).then((res) => res?.results),
    ])
    const [practiceList, providerList] = results.map((result) => {
      if (result.status === 'fulfilled') {
        return result.value
      }
      logger.error('[SSO Login] Failed to fetch practice/provider list:', result.reason)
      return null
    })

    return {
      access_token: token_holder.access_token,
      refresh_token: token_holder.refresh_token,
      result_code: result_code,
      practiceList,
      providerList,
      user: authUser.user,
    }
  } catch (err) {
    logger.error('[SSO Login] Failed:', err.message)
    return thunkAPI.rejectWithValue({ message: err.message })
  }
})

export const mfaLogin = createAsyncThunk('user/mfaLogin', async (credentials, thunkAPI) => {
  try {
    const loginRes = await verifyMfaPasscode({
      mfa_passcode: credentials.code,
      mfa_token: credentials.mfa_token,
    })
    if (loginRes?.access_token) {
      let loginData = await processLogin(loginRes)

      thunkAPI.dispatch(getProviderFavCPT())
      thunkAPI.dispatch(getProviderFavDX())
      thunkAPI.dispatch(getProviderCodeTemplates())

      return loginData
    }

    return loginRes?.data?.type
  } catch (err) {
    console.log('MFA login failed::', err.message)
    return err.message
  }
})

export const userSettingGet = createAsyncThunk('user/usersettingsget', async () => {
  const settingsRes = await getFromSettingsServer(SETTINGS_URL.USER_SETTINGS)
    .then((res) => res?.results)
    .catch((err) => {
      console.error(err)
    })
  return {
    settingsRes,
  }
})
export const userSettingPut = createAsyncThunk('user/usersettingput', async (data) => {
  return putDataToSettingsServer(SETTINGS_URL.USER_SETTINGS, data)
    .then(() => data)
    .catch((err) => {
      console.error(err)
      return null
    })
})

export const providerLogin = createAsyncThunk('user/providerLogin', async (credentials) => {
  const logRes = await setPracticeProvider({
    practice_id: credentials.practice_id,
    provider_id: credentials.provider_id,
  })

  if (logRes?.user_id !== null) {
    const results = await Promise.allSettled([
      getFromSettingsServer(SETTINGS_URL.CURRENT_PROVIDER).then((res) => res?.results),
      getFromSettingsServer(SETTINGS_URL.CURRENT_PRACTICE).then((res) => res?.result),
      // able to replace?
      getFromSettingsServer(SETTINGS_URL.USER_SETTINGS).then((res) => res?.results),
      getFromSettingsServer(SETTINGS_URL.USER_ROLE).then(
        (res) => res?.results?.map((x) => x.groupname) || [],
      ),
      getFromSettingsServer(SETTINGS_URL.USER_PERMISSION).then((res) => res?.results),
      getFromSettingsServer(SETTINGS_URL.USER_FEATURE).then((res) => res),
      getFromSettingsServer(SETTINGS_URL.FAV_PROCEDURE).then((res) =>
        res?.results?.reduce((acc, cpt) => {
          acc[cpt.code] = cpt
          return acc
        }, {}),
      ),
      getFromSettingsServer(SETTINGS_URL.FAV_DIAGNOSIS).then((res) =>
        res?.results?.reduce((acc, dx) => {
          acc[dx.code] = dx
          return acc
        }, {}),
      ),
      getFromSettingsServer(SETTINGS_URL.GET_CODETEMPLATES).then((res) => res?.results),
    ])
    const [
      providerRes,
      practiceRes,
      settingsRes,
      rolesRes,
      permissionsRes,
      featuresRes,
      favCPTs,
      favDX,
      provCodeTemplates,
    ] = results.map((result) => {
      if (result.status === 'fulfilled') {
        return result.value
      }
      console.error(result.reason)
      return null
    })

    return {
      practiceRes,
      providerRes,
      settingsRes,
      rolesRes,
      permissionsRes,
      featuresRes,
      favCPTs,
      favDX,
      provCodeTemplates,
    }
  } else {
    return 'Server Error'
  }
})

export const getProvider = createAsyncThunk('user/getProvider', async () => {
  return await getFromSettingsServer(SETTINGS_URL.CURRENT_PROVIDER)
    .then((res) => res?.results)
    .catch((err) => {
      console.error(err)
      return null
    })
})

export const getProviderFavCPT = createAsyncThunk('user/getProviderFavCPT', async () => {
  return await getFromSettingsServer(SETTINGS_URL.FAV_PROCEDURE)
    .then((res) =>
      res?.results?.reduce((acc, cpt) => {
        acc[cpt.code] = cpt
        return acc
      }, {}),
    )
    .catch((err) => {
      console.error(err)
      return null
    })
})

export const getProviderFavDX = createAsyncThunk('user/getProviderFavDX', async () => {
  return await getFromSettingsServer(SETTINGS_URL.FAV_DIAGNOSIS)
    .then((res) =>
      res?.results?.reduce((acc, dx) => {
        acc[dx.code] = dx
        return acc
      }, {}),
    )
    .catch((err) => {
      console.error(err)
      return null
    })
})

export const getProviderCodeTemplates = createAsyncThunk(
  'user/getProviderCodeTemplates',
  async () => {
    return await getFromSettingsServer(SETTINGS_URL.GET_CODETEMPLATES)
      .then((res) => res?.results?.filter((ct) => ct.enabled))
      .catch((err) => {
        console.error(err)
        return null
      })
  },
)

export const rememberAuth = createAsyncThunk('user/rememberAuth', async (token, thunkAPI) => {
  try {
    const results = await Promise.allSettled([
      getPractice().then((res) => res?.results),
      getProviders().then((res) => res?.results),
      currentSettings().then((res) => res?.results),
      currentRoles().then((res) => res?.results?.map((x) => x.groupname) || []),
      currentPermissions().then((res) => res?.results),
      currentFeatures().then((res) => res),
      currentProvider().then((res) => res?.results),
      currentPractice().then((res) => res?.result),
    ])
    const [
      practiceRes,
      providerRes,
      settingsRes,
      rolesRes,
      permissionsRes,
      featuresRes,
      currentProviderRes,
      currentPracticeRes,
    ] = results.map((result) => {
      if (result.status === 'fulfilled') {
        return result.value
      }
      console.error(result.reason)
      return null
    })

    if (practiceRes?.length > 0 && providerRes?.length) {
      thunkAPI.dispatch(getProviderFavCPT())
      thunkAPI.dispatch(getProviderFavDX())
      thunkAPI.dispatch(getProviderCodeTemplates())

      // TODO get user instead of using a token
      return {
        ...token,
        practiceRes,
        providerRes,
        currentProviderRes,
        currentPracticeRes,
        settingsRes,
        rolesRes,
        permissionsRes,
        featuresRes,
      }
    } else {
      return thunkAPI.rejectWithValue('Server Error')
    }
  } catch (e) {
    if (e?.response?.status === 401) {
      return thunkAPI.rejectWithValue('Unauthorized')
    }
  }
})
